import { Input } from '~/components/ui/input'
import { cn } from '~/lib/utils'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

export interface SuffixInputProps
  extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'type'> {
  value?: number
  onChange?: (value: number | undefined) => void
  onBlur?: () => void
  suffix: string
  formatValue: (value: number | undefined) => string
  parseValue: (value: string) => number | undefined
}

export const SuffixInput = forwardRef<HTMLInputElement, SuffixInputProps>(
  ({ value, onChange, onBlur, className, suffix, formatValue, parseValue, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState<string>('')
    const inputRef = useRef<HTMLInputElement>(null)
    const isFocusedRef = useRef(false)

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    useEffect(() => {
      if (!isFocusedRef.current) {
        const formatted = formatValue(value)
        setDisplayValue(formatted)
      }
    }, [value, formatValue])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value

      if (inputValue === '') {
        setDisplayValue('')
        onChange?.(undefined)
        return
      }

      setDisplayValue(inputValue)

      const parsed = parseValue(inputValue)
      onChange?.(parsed)
    }

    const handleFocus = () => {
      isFocusedRef.current = true
    }

    const handleBlur = () => {
      isFocusedRef.current = false
      const parsed = parseValue(displayValue)
      if (parsed !== undefined) {
        const formatted = formatValue(parsed)
        setDisplayValue(formatted)
        onChange?.(parsed)
      } else {
        setDisplayValue('')
        onChange?.(undefined)
      }
      onBlur?.()
    }

    const suffixWidth = suffix.length > 1 ? 'pr-10' : 'pr-8'

    return (
      <div className="relative">
        <Input
          {...props}
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(suffixWidth, className)}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none">
          {suffix}
        </span>
      </div>
    )
  }
)

SuffixInput.displayName = 'SuffixInput'
